/**
 * KARRIER - interaction layer.
 *
 * Each behaviour degrades gracefully without JS:
 *   1. Quick add   - the card form posts to /cart/add normally; JS upgrades it to AJAX.
 *   2. Wishlist    - a local-only "saved" marker held in localStorage.
 *   3. Explorer    - the product "!" utility tabs and hotspots.
 *   4. Mobile cart - upgrades the cart link to the drawer.
 *   5. Top bar     - full-width at rest, pill once the page scrolls.
 */

(function () {
  'use strict';

  var WISHLIST_KEY = 'karrier:wishlist';

  /* ---------------------------------------------------------------- utils */

  function readWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch (error) {
      // A corrupt or blocked store must never break the page.
      console.warn('Karrier: wishlist unreadable, starting empty.', error);
      return [];
    }
  }

  function writeWishlist(ids) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(ids));
    } catch (error) {
      console.warn('Karrier: wishlist could not be saved.', error);
    }
  }

  /* ------------------------------------------------------------- wishlist */

  function initWishlist() {
    var saved = readWishlist();

    document.querySelectorAll('[data-karrier-wish]').forEach(function (button) {
      if (button.dataset.karrierBound === 'true') return;
      button.dataset.karrierBound = 'true';

      var id = button.getAttribute('data-karrier-wish');
      button.setAttribute('aria-pressed', saved.indexOf(id) > -1 ? 'true' : 'false');

      button.addEventListener('click', function (event) {
        event.preventDefault();
        var list = readWishlist();
        var index = list.indexOf(id);

        if (index > -1) {
          list.splice(index, 1);
          button.setAttribute('aria-pressed', 'false');
        } else {
          list.push(id);
          button.setAttribute('aria-pressed', 'true');
        }

        writeWishlist(list);
      });
    });
  }

  /* ------------------------------------------------------------ quick add */

  /**
   * Brings a cart button up to date from a fresh render of the
   * cart-icon-bubble section: always the count bubble, and the glyph unless
   * the merchant uploaded their own icon. Replacing the whole button would
   * drop the rail's CART label and any uploaded icon.
   *
   * @param {Element} button the rail or top-bar cart link
   * @param {Element} fresh the rendered cart-icon-bubble section
   */
  function syncCartButton(button, fresh) {
    var staleBubble = button.querySelector('.cart-count-bubble');
    var freshBubble = fresh.querySelector('.cart-count-bubble');

    if (staleBubble) staleBubble.remove();
    if (freshBubble) button.appendChild(freshBubble.cloneNode(true));

    if (button.hasAttribute('data-karrier-cart-custom')) return;

    var staleGlyph = button.querySelector('svg');
    var freshGlyph = fresh.querySelector('svg');
    if (staleGlyph && freshGlyph) staleGlyph.replaceWith(freshGlyph.cloneNode(true));
  }

  function refreshCartUI() {
    var bubble = document.getElementById('cart-icon-bubble');
    var drawer = document.querySelector('cart-drawer');
    var sections = ['cart-icon-bubble'];

    if (drawer) sections.push('cart-drawer');

    return fetch(window.Shopify.routes.root + '?sections=' + sections.join(','))
      .then(function (response) {
        if (!response.ok) throw new Error('Section render failed: ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var parser = new DOMParser();
        var freshIcon = data['cart-icon-bubble']
          ? parser
              .parseFromString(data['cart-icon-bubble'], 'text/html')
              .querySelector('#shopify-section-cart-icon-bubble')
          : null;

        if (freshIcon) {
          var mobileCart = document.querySelector('[data-karrier-cart]');
          if (bubble) syncCartButton(bubble, freshIcon);
          if (mobileCart) syncCartButton(mobileCart, freshIcon);
        }

        if (drawer && data['cart-drawer']) {
          drawer.classList.remove('is-empty');
          var freshInner = parser
            .parseFromString(data['cart-drawer'], 'text/html')
            .querySelector('.drawer__inner');
          var target = drawer.querySelector('.drawer__inner');
          if (freshInner && target) target.innerHTML = freshInner.innerHTML;
          if (typeof drawer.open === 'function') drawer.open();
        }
      })
      .catch(function (error) {
        // The item is already in the cart; only the UI refresh failed.
        console.warn('Karrier: cart UI could not refresh, reloading.', error);
        window.location.reload();
      });
  }

  function initQuickAdd() {
    document.querySelectorAll('form[data-karrier-quick-add]').forEach(function (form) {
      if (form.dataset.karrierBound === 'true') return;
      form.dataset.karrierBound = 'true';

      form.addEventListener('submit', function (event) {
        if (!window.fetch || !window.Shopify || !window.Shopify.routes) return;

        event.preventDefault();
        var button = form.querySelector('[type="submit"]');
        // Write to the label span when there is one, so the "+" glyph beside
        // it survives the ADDING / ADDED swap.
        var label = button ? button.querySelector('[data-quick-label]') || button : null;
        var original = label ? label.textContent : '';

        if (button) {
          button.setAttribute('disabled', 'disabled');
          label.textContent = button.getAttribute('data-adding-text') || 'ADDING';
        }

        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: new FormData(form)
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Add to cart failed: ' + response.status);
            return response.json();
          })
          .then(function () {
            if (button) label.textContent = button.getAttribute('data-added-text') || 'ADDED';
            return refreshCartUI();
          })
          .catch(function (error) {
            console.error('Karrier: quick add failed.', error);
            if (button) label.textContent = 'UNAVAILABLE';
          })
          .then(function () {
            window.setTimeout(function () {
              if (!button) return;
              button.removeAttribute('disabled');
              label.textContent = original;
            }, 1800);
          });
      });
    });
  }

  /* ------------------------------------------------------------- explorer */

  function initExplorers() {
    document.querySelectorAll('[data-karrier-explorer]').forEach(function (explorer) {
      if (explorer.dataset.karrierBound === 'true') return;
      explorer.dataset.karrierBound = 'true';

      var triggers = explorer.querySelectorAll('[data-explorer-target]');
      var panelLabel = explorer.querySelector('[data-explorer-label]');
      var panelBody = explorer.querySelector('[data-explorer-body]');

      if (!triggers.length || !panelLabel || !panelBody) return;

      function select(id) {
        triggers.forEach(function (trigger) {
          var isMatch = trigger.getAttribute('data-explorer-target') === id;
          trigger.setAttribute('aria-selected', isMatch ? 'true' : 'false');

          if (!isMatch) return;
          panelLabel.textContent = trigger.getAttribute('data-explorer-title') || '';
          panelBody.textContent = trigger.getAttribute('data-explorer-body') || '';
        });
      }

      triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          select(trigger.getAttribute('data-explorer-target'));
        });
      });

      select(triggers[0].getAttribute('data-explorer-target'));
    });
  }

  /* ----------------------------------------------------- mobile cart button */

  function initMobileCart() {
    var button = document.querySelector('[data-karrier-cart]');
    var drawer = document.querySelector('cart-drawer');

    if (!button || !drawer || button.dataset.karrierBound === 'true') return;
    button.dataset.karrierBound = 'true';

    button.addEventListener('click', function (event) {
      if (typeof drawer.open !== 'function') return;
      event.preventDefault();
      drawer.open(button);
    });
  }

  /* -------------------------------------------------------------- buy now */

  /**
   * The mobile sticky bar buys in one tap: add the variant, then go straight
   * to checkout. Left alone it is a plain submit inside the product form, so
   * without JS the customer still gets an add to cart.
   */
  function initBuyNow() {
    document.querySelectorAll('[data-karrier-buy-now]').forEach(function (button) {
      if (button.dataset.karrierBound === 'true') return;
      button.dataset.karrierBound = 'true';

      button.addEventListener('click', function (event) {
        // Same guard as quick add: without these, leave the plain submit alone.
        if (!window.fetch || !window.Shopify || !window.Shopify.routes) return;

        var form = button.form || document.getElementById(button.getAttribute('form'));
        if (!form) return;

        event.preventDefault();
        if (button.disabled) return;

        button.setAttribute('aria-busy', 'true');

        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Add to cart failed: ' + response.status);
            window.location.href = window.Shopify.routes.root + 'checkout';
          })
          .catch(function (error) {
            // Surface the failure instead of leaving a dead button.
            button.removeAttribute('aria-busy');
            console.error('[karrier] buy now', error);
            form.requestSubmit ? form.requestSubmit() : form.submit();
          });
      });
    });
  }

  /* ------------------------------------------------------- mobile top bar */

  /**
   * The bar spans the full width at rest and retracts into a pill once the
   * page moves. The two states differ only by a data attribute; the CSS
   * animates between them.
   */
  function initTopbarScroll() {
    var section = document.querySelector('.k-nav-section');

    if (!section || section.dataset.karrierScrollBound === 'true') return;
    section.dataset.karrierScrollBound = 'true';

    // A band rather than a single threshold, so a bar parked right at the
    // boundary cannot flip back and forth on sub-pixel scrolls.
    var ENTER_AT = 24;
    var LEAVE_AT = 4;
    var ticking = false;

    function apply() {
      ticking = false;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var scrolled = section.dataset.scrolled === 'true';

      if (!scrolled && y > ENTER_AT) section.dataset.scrolled = 'true';
      else if (scrolled && y < LEAVE_AT) section.dataset.scrolled = 'false';
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    initTopbarScroll();
    initBuyNow();
    initWishlist();
    initQuickAdd();
    initExplorers();
    initMobileCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders sections in place; re-bind what it replaced.
  document.addEventListener('shopify:section:load', boot);
})();
